import {
  AlertTriangle,
  Copy,
  Lightbulb,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import type { LabVisit } from "@/lib/api_client";
import {
  useConsultationFollowupSuggestions,
  useRegenerateConsultationFollowupSuggestions,
} from "@/hooks/api/useConsultationFollowupSuggestions";

const REGENERATION_ELIGIBLE_STATUSES: ReadonlyArray<LabVisit["status"]> = [
  "bloodTestRequested",
  "pendingRequisition",
  "requisitioned",
  "awaitingResults",
];

export function ConsultationFollowupSuggestions({
  labVisit,
}: {
  labVisit: LabVisit;
}) {
  const { data, isLoading, isError, refetch } =
    useConsultationFollowupSuggestions(labVisit.id);
  const regenerate = useRegenerateConsultationFollowupSuggestions();

  const canRegenerate = REGENERATION_ELIGIBLE_STATUSES.includes(
    labVisit.status,
  );

  // Backend returns row: null when no cached suggestions exist AND the
  // visit's lifecycle state isn't eligible for generation (e.g. `paid`,
  // or after results have landed). Hide the card entirely in that case.
  if (!isLoading && !isError && data === null) return null;

  const handleRegenerate = async () => {
    try {
      await regenerate.mutateAsync(labVisit.id);
      toast.success("Nye forslag generert");
    } catch {
      toast.error("Klarte ikke å generere nye forslag");
    }
  };

  const copyText = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
    } catch {
      toast.error("Klarte ikke å kopiere");
    }
  };

  const handleCopyAll = () => {
    if (!data) return;
    const text = data.suggestions
      .map((s, i) => `${i + 1}. ${s.question}`)
      .join("\n");
    copyText(text, "Spørsmål kopiert");
  };

  const header = (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-admin-warn-300">
        <Sparkles className="h-3.5 w-3.5" />
        Foreslåtte oppfølgingsspørsmål
      </div>
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-[10px] text-admin-text-subtle hover:text-admin-text-primary"
          onClick={handleCopyAll}
          disabled={isLoading || !data || regenerate.isPending}
          title="Kopier alle spørsmål"
        >
          <Copy className="h-3 w-3 mr-1" />
          Kopier
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-[10px] text-admin-text-subtle hover:text-admin-text-primary"
          onClick={handleRegenerate}
          disabled={regenerate.isPending || isLoading || !canRegenerate}
          title={
            canRegenerate
              ? "Generer nye forslag"
              : "Regenerering er kun mulig før prøvesvar er mottatt"
          }
        >
          <RefreshCw
            className={`h-3 w-3 mr-1 ${regenerate.isPending ? "animate-spin" : ""}`}
          />
          Generer på nytt
        </Button>
      </div>
    </div>
  );

  return (
    <Card className="border-l-4 border-l-admin-warn-400/70">
      <CardContent className="p-2.5 space-y-2">
        {header}
        {isLoading && (
          <div className="space-y-1.5">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-12 rounded bg-admin-card-hover/40 animate-pulse"
              />
            ))}
            <p className="text-[10px] text-admin-text-subtle italic">
              Genererer forslag …
            </p>
          </div>
        )}
        {!isLoading && (isError || !data) && (
          <div className="space-y-1.5">
            <p className="text-xs text-admin-negative">
              Klarte ikke å hente forslag.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => refetch()}
            >
              Prøv igjen
            </Button>
          </div>
        )}
        {!isLoading && data && data.model === "default-fallback" && (
          <div className="flex items-start gap-1.5 rounded border border-admin-warn-500/40 bg-admin-warn-500/10 p-2">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-admin-warn-300 shrink-0" />
            <p className="text-[11px] text-admin-warn-100 leading-relaxed">
              <span className="font-semibold">Standard-spørsmål.</span> Vi har
              ingen helseskjema-svar eller tidligere prøvesvar for denne
              pasienten — spørsmålene under er generiske åpninger, ikke
              tilpasset.
            </p>
          </div>
        )}
        {!isLoading && data && (
          <div
            className={`space-y-1.5 ${regenerate.isPending ? "opacity-50" : ""}`}
          >
            {data.suggestions.map((s, i) => (
              <div
                key={i}
                className="border border-admin-card-border rounded p-2 group"
              >
                <div className="flex items-start gap-1.5">
                  <Lightbulb className="h-3 w-3 mt-0.5 text-admin-warn-400/70 shrink-0" />
                  <div className="flex-1 space-y-1">
                    <p className="text-xs text-admin-text-primary leading-relaxed">
                      {i + 1}. {s.question}
                    </p>
                    <p className="text-[10px] text-admin-text-subtle italic">
                      {s.rationale}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0 shrink-0 text-admin-text-subtle hover:text-admin-text-primary opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                    onClick={() => copyText(s.question, "Spørsmål kopiert")}
                    title="Kopier dette spørsmålet"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
